/**
 * E002-T034: ExecutionPanel Component
 *
 * Displays execution controls and progress:
 * - Test Run / Execute buttons
 * - Pause / Resume / Stop controls
 * - Progress bar with statistics
 * - Execution results summary
 *
 * E002-T040: E2E Integration
 * - Persists completed logs to Block-Model
 * - Updates lastRunAt/lastRunStatus in Block-Model
 * - Creates InputStageLog from processed rows
 * - Calls workflow engine webhook
 */

import { Button, notify, Tooltip } from '@affine/component';
import { useUiOfflineSignal } from '@affine/core/components/hooks/use-connectivity-status';
import { useI18n } from '@affine/i18n';
import type { BlockModel } from '@blocksuite/affine/store';
import { CloseIcon, PlayIcon } from '@blocksuite/icons/rc';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { workspaceApiFetch } from '../../../cloud';
import { WorkspaceDialogService } from '../../../dialogs';
import { EditorService } from '../../../editor';
import { WorkspaceService } from '../../../workspace';
import { isAiKeyError } from '../../hooks/is-ai-key-error';
import { extractConfig } from '../../hooks/use-block-model-config';
import type { AutomationConfig } from '../../services/automation';
import {
  AutomationConfigApiService,
  isVerifySyncBackendUnavailable,
  type VerifySyncConfig,
} from '../../services/automation-config-api';
import type { DatabaseInfo } from '../../services/database-discovery';
import type { ProcessedRows } from '../../services/database-rows';
import {
  ExecutionService,
  type ExecutionState,
  type ExecutionStatus,
  type ExecutionWarning,
} from '../../services/execution';
import {
  LoggingService,
  type ProcessStageRowEntry,
} from '../../services/logging';
import { type WorkflowInfo, WorkflowService } from '../../services/workflow';
import { WorkflowClientService } from '../../services/workflow-client';
import { WorkflowTestContextService } from '../../services/workflow-test-context';
import { workflowWorkspaceRoutes } from '../../services/workflow-workspace-routes';
import {
  fetchWorkflowLimits,
  type WorkflowLimits,
} from '../../utils/workflow-limits';
import * as styles from '../automation-panel.css';
import { ExecutionRowList } from './execution-row-list';
// =============================================================================
// E029-PREFLIGHT: Pre-Flight Types & Comparison (extracted for testability)
// =============================================================================
import {
  buildPreFlightAttributes,
  collectPreFlightDocIds,
  flushPreFlightDocIds,
  getPreFlightDisplayStatus,
  getStaleWorkflowMappingIds,
  type PreFlightAttribute,
  type PreFlightProfile,
  type PreFlightResult,
  type PreFlightStep,
} from './preflight-comparison';
import type { RunRequirement } from './run-requirements-dialog';
import { RunRequirementsDialog } from './run-requirements-dialog';
import { WarningRow } from './warning-row';

interface ExecutionPanelProps {
  /** Block ID for this execution */
  blockId: string;
  /** Whether the automation is configured and ready to run */
  isConfigured: boolean;
  /** Rows to execute (from source table with filters applied) */
  rows?: Array<Record<string, unknown>>;
  /** Processed rows data for logging */
  processedRows?: ProcessedRows | null;
  /** Selected workflow */
  workflow?: WorkflowInfo | null;
  /** Automation config */
  config?: AutomationConfig | null;
  /** Database discovery data used to flush sharded storage root docs. */
  accessibleDatabases?: DatabaseInfo[];
  /** sl-3yai.7.2: Whether compliance guard allows full execution */
  canExecute?: boolean;
  /** Action items for the configure hint (replaces BindingGuardBanner) */
  actionItems?: Array<{ label: string; onClick: () => void }>;
}

// =============================================================================
// Hook to get BlockModel
// =============================================================================

function useAutomationBlockModel(blockId: string | null): BlockModel | null {
  const editorService = useServiceOptional(EditorService);
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);

  return useMemo(() => {
    if (!blockId || !editorContainer) return null;
    const doc = editorContainer.doc;
    return doc?.getBlock(blockId)?.model ?? null;
  }, [blockId, editorContainer]);
}

// =============================================================================
// Sub-Components
// =============================================================================

import type { BadgeStatus } from '../../hooks';

/**
 * E018-S007-T001: StatusBadge using BadgeStatus from useAutomationUIState
 * Displays the unified UI status badge
 */
export const StatusBadge: FC<{ status: BadgeStatus }> = ({ status }) => {
  const t = useI18n();

  const colors: Record<BadgeStatus, { bg: string; text: string }> = {
    ready: {
      bg: 'var(--streamlain-success-color)',
      text: 'white',
    },
    running: { bg: 'var(--streamlain-primary-color)', text: 'white' },
    queued: { bg: 'var(--streamlain-warning-color)', text: 'white' },
    paused: { bg: 'var(--streamlain-warning-color)', text: 'white' },
    completed: { bg: 'var(--streamlain-success-color)', text: 'white' },
    cancelled: {
      bg: 'var(--streamlain-text-secondary-color)',
      text: 'white',
    },
    failed: { bg: 'var(--streamlain-error-color)', text: 'white' },
  };

  const i18nStatusKey: Partial<Record<BadgeStatus, string>> = {
    ready: 'com.streamlain.streamlain.execution.status.ready',
    running: 'com.streamlain.streamlain.execution.status.running',
    queued: 'com.streamlain.streamlain.history.queued',
    paused: 'com.streamlain.streamlain.execution.status.paused',
    completed: 'com.streamlain.streamlain.execution.status.completed',
    failed: 'com.streamlain.streamlain.execution.status.failed',
  };

  const i18nTooltipKey: Partial<Record<BadgeStatus, string>> = {
    ready: 'com.streamlain.streamlain.execution.tooltip.ready',
    failed: 'com.streamlain.streamlain.execution.tooltip.failed',
  };

  const color = colors[status];
  const tooltipKey = i18nTooltipKey[status];

  return (
    <span
      data-testid="automation-execution-status"
      data-status={status}
      style={{
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: '4px',
        backgroundColor: color.bg,
        color: color.text,
        textTransform: 'uppercase',
        cursor: tooltipKey ? 'help' : undefined,
      }}
      title={tooltipKey ? t[tooltipKey as keyof typeof t]() : undefined}
    >
      {status === 'cancelled'
        ? 'Cancelled'
        : t[i18nStatusKey[status] as keyof typeof t]()}
    </span>
  );
};

const TestRunConsentDialog: FC<{
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, loading, onCancel, onConfirm }) => {
  const t = useI18n();
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0, 0, 0, 0.36)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div
        style={{
          width: 'min(520px, calc(100vw - 32px))',
          borderRadius: '12px',
          border: `1px solid ${cssVarV2('layer/insideBorder/border')}`,
          background: cssVarV2('layer/background/primary'),
          boxShadow: '0 18px 60px rgba(0, 0, 0, 0.22)',
          padding: '20px',
          color: cssVarV2('text/primary'),
        }}
      >
        <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: 8 }}>
          {t['com.streamlain.streamlain.execution.test.dialog-title']?.() ??
            'Testdaten bereitstellen'}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            fontSize: '13px',
            lineHeight: 1.45,
            color: cssVarV2('text/secondary'),
          }}
        >
          <div>
            {t['com.streamlain.streamlain.execution.test.dialog-body']?.() ??
              `${BRAND_DISPLAY_NAME} liest Beispielzeilen aus der aktuell konfigurierten Automation und macht sie im Workflow Builder als Test-Kontext verfügbar.`}
          </div>
          <div>
            {t[
              'com.streamlain.streamlain.execution.test.dialog-provenance'
            ]?.() ??
              'Sichtbar bleiben diese Daten nur für Personen mit Zugriff auf das Quell-Dokument. Es werden keine Output-Datenbanken beschrieben und kein Status-Tracking aktualisiert.'}
          </div>
          <div>
            {t[
              'com.streamlain.streamlain.execution.test.dialog-retention'
            ]?.() ?? 'Testdaten werden nach 30 Tagen entfernt.'}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 20,
          }}
        >
          <Button disabled={loading} onClick={onCancel}>
            {t['com.affine.cancel']?.() ?? 'Abbrechen'}
          </Button>
          <Button variant="primary" disabled={loading} onClick={onConfirm}>
            {loading
              ? (t['com.streamlain.streamlain.execution.test.creating']?.() ??
                'Bereite vor...')
              : (t['com.streamlain.streamlain.execution.test.confirm']?.() ??
                'Testdaten bereitstellen')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// E029-PREFLIGHT: Progress Bar with gradient blending + shimmer animation
// =============================================================================

/** Inject shimmer keyframes once */
let shimmerInjected = false;
function ensureShimmerKeyframes() {
  if (shimmerInjected) return;
  const style = document.createElement('style');
  style.textContent = `
@keyframes sl-bar-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}`;
  document.head.appendChild(style);
  shimmerInjected = true;
}

type ProgressPhase = 'preflight' | 'execution';

/** Resolve a step to a CSS color */
function stepColor(step: PreFlightStep | undefined): string {
  if (!step || step.status === 'pending')
    return 'var(--streamlain-background-tertiary-color)';
  if (step.status === 'failed') return 'var(--streamlain-error-color)';
  if (step.status === 'done') return 'var(--streamlain-success-color)';
  return 'var(--streamlain-primary-color)'; // running
}

// sl-3nkm.8.4: TimeoutState + pure math live in ./timeout-state so unit
// tests can import them without pulling in the lit-loaded component tree.
import { computeTimeoutState, type TimeoutState } from './timeout-state';

const ProgressBar: FC<{
  percentage: number;
  status: ExecutionStatus;
  phase: ProgressPhase;
  preFlightResult: PreFlightResult | null;
  timeoutState?: TimeoutState;
}> = ({ percentage, status, phase, preFlightResult, timeoutState }) => {
  ensureShimmerKeyframes();

  const BAR_HEIGHT = '4px';
  const BAR_RADIUS = '2px';

  // --- Preflight: gradient bar that blends adjacent step colors ---
  if (phase === 'preflight' && preFlightResult) {
    const steps = preFlightResult.steps;
    const colors = steps.map(s => stepColor(s));
    const allDone = steps.every(s => s.status === 'done');
    const anyFailed = steps.some(s => s.status === 'failed');

    // Build a linear-gradient with smooth stops between segments
    // Each segment gets ~25% of the bar, with 2% blend zones
    const stops: string[] = [];
    const segWidth = 100 / colors.length;
    for (let i = 0; i < colors.length; i++) {
      if (i === 0) {
        stops.push(`${colors[i]} 0%`);
        stops.push(`${colors[i]} ${segWidth - 1}%`);
      } else if (i === colors.length - 1) {
        stops.push(`${colors[i]} ${segWidth * i + 1}%`);
        stops.push(`${colors[i]} 100%`);
      } else {
        stops.push(`${colors[i]} ${segWidth * i + 1}%`);
        stops.push(`${colors[i]} ${segWidth * (i + 1) - 1}%`);
      }
    }

    // Overlay: when all done → solid green, when failed → solid red
    // Applied as a semi-transparent overlay that transitions in
    const overlayColor = allDone
      ? 'var(--streamlain-success-color)'
      : anyFailed
        ? 'var(--streamlain-error-color)'
        : 'transparent';
    const overlayOpacity = allDone || anyFailed ? 1 : 0;

    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: BAR_HEIGHT,
          borderRadius: BAR_RADIUS,
          overflow: 'hidden',
        }}
      >
        {/* Gradient base layer */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(90deg, ${stops.join(', ')})`,
            transition: 'background 0.4s ease',
          }}
        />
        {/* Color overlay (green on success, red on failure) */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: overlayColor,
            opacity: overlayOpacity,
            transition: 'opacity 0.6s ease, background-color 0.6s ease',
          }}
        />
      </div>
    );
  }

  // --- Execution phase: fill bar with shimmer when running ---
  const barColor = (() => {
    // sl-3nkm.8.4: Timeout warning/error overrides the running/idle
    // colour so the user sees the pressure before the backend flips
    // the status to 'failed'. Terminal statuses keep their own
    // colour — once the run finished, the timeout window is moot.
    if (
      status === 'running' &&
      (timeoutState === 'warning' || timeoutState === 'error')
    ) {
      return timeoutState === 'error'
        ? 'var(--streamlain-error-color)'
        : 'var(--streamlain-warning-color, #f59e0b)';
    }
    switch (status) {
      case 'running':
        return 'var(--streamlain-primary-color)';
      case 'completed':
        return 'var(--streamlain-success-color)';
      case 'failed':
        return 'var(--streamlain-error-color)';
      default:
        return 'var(--streamlain-text-secondary-color)';
    }
  })();

  const isActive = status === 'running';
  // When running with 0% progress, show indeterminate (full-width shimmer)
  const indeterminate = isActive && percentage === 0;
  const fillWidth = indeterminate ? 100 : percentage;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: BAR_HEIGHT,
        backgroundColor: 'var(--streamlain-background-tertiary-color)',
        borderRadius: BAR_RADIUS,
        overflow: 'hidden',
      }}
    >
      {/* Fill */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${fillWidth}%`,
          height: '100%',
          backgroundColor: barColor,
          opacity: indeterminate ? 0.35 : 1,
          transition: indeterminate
            ? 'none'
            : 'width 0.3s ease, background-color 0.4s ease',
        }}
      />
      {/* Shimmer overlay when running */}
      {isActive && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${fillWidth}%`,
            height: '100%',
            background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)`,
            backgroundSize: '200% 100%',
            animation: 'sl-bar-shimmer 1.8s ease-in-out infinite',
          }}
        />
      )}
    </div>
  );
};

/**
 * sl-3nkm.8.4: Wraps `ProgressBar` with a warning/error tooltip that
 * surfaces the remaining time before the step hits `maxStepDurationMs`.
 * The tooltip appears only when the run is approaching / past the
 * timeout ceiling — normal state stays silent so the UI does not nag
 * during healthy executions.
 */
const TimeoutAwareProgressBar: FC<{
  percentage: number;
  status: ExecutionStatus;
  phase: ProgressPhase;
  preFlightResult: PreFlightResult | null;
  timeoutState: TimeoutState;
  remainingMs: number | null;
}> = ({
  percentage,
  status,
  phase,
  preFlightResult,
  timeoutState,
  remainingMs,
}) => {
  const t = useI18n();
  const bar = (
    <ProgressBar
      percentage={percentage}
      status={status}
      phase={phase}
      preFlightResult={preFlightResult}
      timeoutState={timeoutState}
    />
  );

  if (timeoutState === 'normal' || status !== 'running') {
    return bar;
  }

  const tooltip =
    timeoutState === 'error'
      ? t['com.streamlain.streamlain.execution.timeout.reached']()
      : t['com.streamlain.streamlain.execution.timeout.approaching']({
          remaining: String(Math.ceil((remainingMs ?? 0) / 1000)),
        });

  return (
    <Tooltip content={tooltip} side="top">
      <div style={{ width: '100%' }}>{bar}</div>
    </Tooltip>
  );
};

// =============================================================================
// Elapsed Timer Hook
// =============================================================================

function useElapsedTimer(isRunning: boolean, startTime: number | null): string {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isRunning || !startTime) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning, startTime]);

  if (!startTime) return '0:00';
  const elapsed = Math.max(
    0,
    Math.floor(((isRunning ? now : Date.now()) - startTime) / 1000)
  );
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * sl-3nkm.8.4: Convert the elapsed-since-start signal into a
 * normal/warning/error state gated on the plan's `maxStepDurationMs`.
 * Emits on the same 1-second tick as `useElapsedTimer`; when
 * `maxStepDurationMs` is missing (limits still loading) we return
 * `normal` + `null` so the UI behaves like pre-8.4.
 */
function useTimeoutWarning(
  isRunning: boolean,
  startTime: number | null,
  maxStepDurationMs: number | null
): { state: TimeoutState; remainingMs: number | null } {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isRunning || !startTime || !maxStepDurationMs) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning, startTime, maxStepDurationMs]);

  if (!isRunning || !startTime || !maxStepDurationMs) {
    return { state: 'normal', remainingMs: null };
  }
  const elapsedMs = Math.max(0, now - startTime);
  return computeTimeoutState(elapsedMs, maxStepDurationMs);
}

// =============================================================================
// E029-PREFLIGHT: Status indicator + Stats Line (clean, no glow)
// =============================================================================

/** Clean status color — no text-shadow, just subtle color differentiation */
const statusColor = (
  active: boolean,
  done: boolean,
  failed: boolean
): string => {
  if (failed) return 'var(--streamlain-error-color)';
  if (active) return 'var(--streamlain-primary-color)';
  if (done) return 'var(--streamlain-success-color)';
  return 'var(--streamlain-text-disable-color)';
};

/** Small filled circle as status dot */
const StatusDot: FC<{ color: string }> = ({ color }) => (
  <span
    style={{
      display: 'inline-block',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: color,
      marginRight: '4px',
      flexShrink: 0,
    }}
  />
);

type I18nRecord = Record<
  string,
  ((params?: Record<string, string>) => string) | undefined
>;

function text(
  t: unknown,
  key: string,
  fallback: string,
  params?: Record<string, string>
): string {
  return (t as I18nRecord)[key]?.(params) ?? fallback;
}

function userFacingExecutionError(t: unknown, error: string): string {
  const normalized = error.toLowerCase();
  if (
    normalized.includes('warm_pool_enabled') ||
    normalized.includes('warm pool dispatch') ||
    normalized.includes('k8s mode')
  ) {
    return text(
      t,
      'com.streamlain.streamlain.execution.error.runner-unavailable',
      'Die Ausführung ist gerade nicht verfügbar. Bitte später erneut versuchen.'
    );
  }
  if (
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('connection timeout') ||
    normalized.includes('backend unreachable')
  ) {
    return text(
      t,
      'com.streamlain.streamlain.execution.error.backend-unavailable',
      'Der Backend-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.'
    );
  }
  return error;
}

const ProgressStats: FC<{
  state: ExecutionState;
  preFlightResult: PreFlightResult | null;
  phase: ProgressPhase;
  onPreFlightClick: () => void;
  elapsedTime: string;
}> = ({ state, preFlightResult, phase, onPreFlightClick, elapsedTime }) => {
  const t = useI18n();
  const { progress } = state;

  const preFlightStatus = getPreFlightDisplayStatus(preFlightResult);
  const preFlightDone = preFlightStatus === 'done';
  const preFlightFailed = preFlightStatus === 'failed';
  const preFlightRunning = preFlightStatus === 'running';

  const executionActive = phase === 'execution' && state.status === 'running';
  const executionDone =
    phase === 'execution' &&
    (state.status === 'completed' || state.status === 'cancelled');
  const executionFailed = phase === 'execution' && state.status === 'failed';

  const pfColor = statusColor(preFlightRunning, preFlightDone, preFlightFailed);
  const exColor = statusColor(executionActive, executionDone, executionFailed);

  const pfLabel = preFlightFailed
    ? t['com.streamlain.streamlain.execution.preflight.failed']()
    : preFlightDone
      ? t['com.streamlain.streamlain.execution.preflight.ok']()
      : preFlightRunning
        ? t['com.streamlain.streamlain.execution.preflight.verifying']()
        : text(
            t,
            'com.streamlain.streamlain.execution.preflight',
            'Vorbereitung'
          );

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '11px',
        color: 'var(--streamlain-text-secondary-color)',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {/* Preflight Status (clickable) */}
      <span
        onClick={onPreFlightClick}
        style={{
          cursor: preFlightResult ? 'pointer' : 'default',
          color: pfColor,
          display: 'flex',
          alignItems: 'center',
          fontWeight: 500,
        }}
      >
        <StatusDot color={pfColor} />
        {pfLabel}
      </span>

      {/* Row progress + results (always visible, dimmed when inactive) */}
      <span
        style={{
          color: exColor,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <StatusDot color={exColor} />
        {t['com.streamlain.streamlain.execution.rows']({
          processed: String(progress.processed),
          total: String(progress.total),
        })}
      </span>

      {progress.media && (
        <span
          style={{
            color: executionActive
              ? 'var(--streamlain-primary-color)'
              : 'var(--streamlain-text-secondary-color)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {[
            progress.media.percent !== undefined
              ? `${progress.media.percent}%`
              : null,
            progress.media.bitrate ?? null,
            progress.media.fps !== undefined
              ? `${progress.media.fps.toFixed(1)} fps`
              : null,
            progress.media.timeRemainingMs !== undefined
              ? `ETA ${Math.max(0, Math.ceil(progress.media.timeRemainingMs / 1000))}s`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}

      {/* Elapsed time — always visible, shows 0:00 when idle */}
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          color: executionActive
            ? 'var(--streamlain-primary-color)'
            : 'var(--streamlain-text-disable-color)',
          fontWeight: 500,
          minWidth: '36px',
          textAlign: 'right',
        }}
      >
        {elapsedTime}
      </span>
    </div>
  );
};

// =============================================================================
// E029-PREFLIGHT: Expandable Detail Table
// =============================================================================

const PreFlightExpandable: FC<{
  result: PreFlightResult;
  expanded: boolean;
}> = ({ result, expanded }) => {
  const t = useI18n();

  const handleCopy = useCallback(() => {
    const lines: string[] = [];
    lines.push(`Pre-Flight Verification (${result.profile})`);
    lines.push('');
    for (const step of result.steps) {
      const dur = step.durationMs !== undefined ? ` ${step.durationMs}ms` : '';
      const icon =
        step.status === 'done'
          ? '[ok]'
          : step.status === 'failed'
            ? '[x]'
            : '[ ]';
      lines.push(`${icon} ${step.name}${dur}`);
      if (step.error) lines.push(`  Error: ${step.error}`);
    }
    lines.push('');
    lines.push(
      `Attributes: ${result.matchedAttributes}/${result.totalAttributes}`
    );
    lines.push('');
    lines.push('Attribute            Local              Backend            OK');
    lines.push('-'.repeat(72));
    for (const attr of result.attributes) {
      const icon = attr.match ? '[ok]' : '[x]';
      const l = attr.localValue.padEnd(18).slice(0, 18);
      const b = attr.backendValue.padEnd(18).slice(0, 18);
      lines.push(`${attr.name.padEnd(20)} ${l} ${b} ${icon}`);
    }
    navigator.clipboard
      .writeText(lines.join('\n'))
      .then(() => {
        notify.success({
          title: t['com.streamlain.streamlain.execution.preflight.copied'](),
          message:
            t['com.streamlain.streamlain.execution.preflight.copied-message'](),
        });
      })
      .catch(() => {
        /* clipboard write failed */
      });
  }, [result, t]);

  if (!expanded) return null;

  return (
    <div
      style={{
        marginTop: '8px',
        padding: '10px',
        backgroundColor: 'var(--streamlain-background-secondary-color)',
        borderRadius: '8px',
        fontSize: '11px',
        fontFamily: 'monospace',
        lineHeight: 1.6,
        overflow: 'auto',
        maxHeight: '280px',
      }}
    >
      {/* Header with copy button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {t['com.streamlain.streamlain.execution.preflight.title']({
            profile: result.profile,
          })}
        </span>
        <button
          onClick={handleCopy}
          style={{
            background: 'none',
            border: '1px solid var(--streamlain-border-color)',
            borderRadius: '4px',
            padding: '2px 6px',
            cursor: 'pointer',
            fontSize: '10px',
            color: 'var(--streamlain-text-secondary-color)',
          }}
        >
          {t['com.streamlain.streamlain.execution.preflight.copy']()}
        </button>
      </div>

      {/* Phase steps */}
      {result.steps.map((step, i) => {
        const dotColor =
          step.status === 'done'
            ? 'var(--streamlain-success-color)'
            : step.status === 'failed'
              ? 'var(--streamlain-error-color)'
              : step.status === 'running'
                ? 'var(--streamlain-primary-color)'
                : 'var(--streamlain-text-disable-color)';
        const textColor =
          step.status === 'done'
            ? 'var(--streamlain-success-color)'
            : step.status === 'failed'
              ? 'var(--streamlain-error-color)'
              : 'var(--streamlain-text-secondary-color)';
        return (
          <div
            key={i}
            style={{
              color: textColor,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <StatusDot color={dotColor} />
            {t['com.streamlain.streamlain.execution.preflight.phase']({
              number: String(i + 1),
              name: step.name,
            })}
            {step.durationMs !== undefined && (
              <span style={{ opacity: 0.5 }}> {step.durationMs}ms</span>
            )}
            {step.error && (
              <span style={{ color: 'var(--streamlain-error-color)' }}>
                {' '}
                — {step.error}
              </span>
            )}
          </div>
        );
      })}

      {/* Attribute details */}
      <div
        style={{
          marginTop: '8px',
          borderTop: '1px solid var(--streamlain-border-color)',
          paddingTop: '8px',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
          {t['com.streamlain.streamlain.execution.preflight.attributes']({
            matched: String(result.matchedAttributes),
            total: String(result.totalAttributes),
          })}
        </div>
        {result.attributes.map((attr, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: '8px',
              padding: '2px 0',
              backgroundColor: attr.match
                ? undefined
                : 'rgba(var(--streamlain-error-color-rgb, 255, 76, 76), 0.08)',
              borderRadius: '2px',
            }}
          >
            <span style={{ width: '140px', flexShrink: 0, opacity: 0.8 }}>
              {attr.name}
            </span>
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={attr.localValue}
            >
              {attr.localValue}
            </span>
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={attr.backendValue}
            >
              {attr.backendValue}
            </span>
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: attr.match
                  ? 'var(--streamlain-success-color)'
                  : 'var(--streamlain-error-color)',
                alignSelf: 'center',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const ExecutionPanel: FC<ExecutionPanelProps> = ({
  blockId,
  rows = [],
  workflow,
  config,
  accessibleDatabases = [],
  canExecute = true,
  actionItems,
}) => {
  const executionService = useService(ExecutionService);
  const loggingService = useService(LoggingService);
  const workflowClient = useService(WorkflowClientService);
  const workflowService = useService(WorkflowService);
  const workflowTestContext = useService(WorkflowTestContextService);
  const workspaceService = useService(WorkspaceService);
  const workflowOnline = useLiveData(workflowService.connectivity$);
  const showWorkflowOffline = useUiOfflineSignal(workflowOnline === false);
  const isOnline = !showWorkflowOffline;
  // sl-g3on.5.3: Used by WarningRow to resolve field IDs to display names.
  const selectedWorkflowSchema = useLiveData(
    workflowService.selectedWorkflowSchema$
  );
  const workspaceDialogService = useService(WorkspaceDialogService);
  const editorService = useServiceOptional(EditorService);
  const blockModel = useAutomationBlockModel(blockId);
  const state = useLiveData(executionService.getState$(blockId));
  const hasActionItems = actionItems != null && actionItems.length > 0;
  const actionBoxRef = useRef<HTMLDivElement>(null);
  const shakeActionBox = useCallback(() => {
    const el = actionBoxRef.current;
    if (!el) return;
    el.style.animation = 'none';
    // Force reflow to restart animation
    void el.offsetWidth;
    el.style.animation = 'shake 0.4s ease-in-out';
    el.addEventListener(
      'animationend',
      () => {
        el.style.animation = '';
      },
      { once: true }
    );
  }, []);

  // Inject shake keyframes once
  useEffect(() => {
    if (document.getElementById('sl-shake-keyframes')) return;
    const style = document.createElement('style');
    style.id = 'sl-shake-keyframes';
    style.textContent = `@keyframes shake { 0%,100% { transform: translateX(0) } 20%,60% { transform: translateX(-4px) } 40%,80% { transform: translateX(4px) } }`;
    document.head.appendChild(style);
  }, []);

  // Get workspace and doc IDs for backend persistence
  const workspaceId = workspaceService.workspace.id;
  const editorContainer = useLiveData(
    editorService?.editor?.editorContainer$ ?? null
  );
  const docId = editorContainer?.doc?.id ?? 'unknown';

  const canStartFromState = executionService.canStart(blockId);
  const canStart = canStartFromState && isOnline;
  const canStop = executionService.canStop(blockId);

  // State for Run Requirements Dialog
  const [requirementsDialogMode, setRequirementsDialogMode] = useState<
    'run' | 'test' | null
  >(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [creatingTestRun, setCreatingTestRun] = useState(false);
  const activeTestRun = useLiveData(workflowTestContext.activeRun$);

  // E029-PREFLIGHT: Pre-Flight state
  const [preFlightResult, setPreFlightResult] =
    useState<PreFlightResult | null>(null);
  const [preFlightExpanded, setPreFlightExpanded] = useState(false);
  const [progressPhase, setProgressPhase] =
    useState<ProgressPhase>('preflight');
  const preFlightRef = useRef<PreFlightResult | null>(null);
  const preFlightDatabaseStorageRefs = useMemo(
    () =>
      accessibleDatabases.map(database => ({
        tableId: database.blockId,
        docId: database.docId,
        databaseRootDocId: database.databaseRootDocId,
        databaseRowShardDocIds: database.databaseRowShardDocIds,
      })),
    [accessibleDatabases]
  );

  // sl-g3on.5.3: Last execution's structured warnings — rendered below the
  // run controls until the next execution clears them.
  const [lastWarnings, setLastWarnings] = useState<ExecutionWarning[] | null>(
    null
  );
  // sl-g3on.9.2: Last execution's per-row retry / on-failure metadata.
  const [lastExecutionRows, setLastExecutionRows] = useState<
    ProcessStageRowEntry[] | null
  >(null);

  const t = useI18n();
  const offlineExecutionTitle = text(
    t,
    'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
    'Ausführung gerade nicht verfügbar'
  );
  const offlineExecutionMessage =
    t['com.streamlain.streamlain.connectivity.offline-warning']();
  const showOfflineExecutionToast = useCallback(() => {
    notify.warning({
      title: offlineExecutionTitle,
      message: offlineExecutionMessage,
    });
  }, [offlineExecutionMessage, offlineExecutionTitle]);
  const hasConfiguredSource =
    config?.triggerConfig?.type === 'webhook'
      ? !!config?.triggerConfig?.webhookCredentialId
      : !!config?.sourceTableId;

  // Calculate requirements for Run button
  const hasOutputMappings = (config?.outputMappings ?? []).some(
    m => !!m.responseField
  );
  const runRequirements: RunRequirement[] = useMemo(() => {
    return [
      {
        id: 'source_table',
        label:
          config?.triggerConfig?.type === 'webhook'
            ? (t[
                'com.streamlain.streamlain.execution.preflight.webhook-source'
              ]?.() ?? 'Webhook source')
            : t['com.streamlain.streamlain.execution.preflight.source'](),
        met: hasConfiguredSource,
        description:
          config?.triggerConfig?.type === 'webhook'
            ? (t[
                'com.streamlain.streamlain.execution.preflight.webhook-source-desc'
              ]?.() ??
              'Select the webhook that should trigger this automation.')
            : t['com.streamlain.streamlain.execution.preflight.source-desc'](),
      },
      {
        id: 'workflow',
        label: t['com.streamlain.streamlain.execution.preflight.workflow'](),
        met: !!config?.workflowId,
        description:
          t['com.streamlain.streamlain.execution.preflight.workflow-desc'](),
      },
      {
        id: 'rows_available',
        label: t['com.streamlain.streamlain.execution.preflight.rows'](),
        met: rows.length > 0,
        description:
          rows.length === 0
            ? t['com.streamlain.streamlain.execution.preflight.rows-desc']()
            : undefined,
      },
      {
        id: 'output_mapping',
        label: t['com.streamlain.streamlain.execution.preflight.mapping'](),
        met: hasOutputMappings,
        description:
          t['com.streamlain.streamlain.execution.preflight.mapping-desc'](),
      },
    ];
  }, [
    hasConfiguredSource,
    config?.triggerConfig?.type,
    config?.workflowId,
    rows.length,
    hasOutputMappings,
    t,
  ]);

  const allRequirementsMet = runRequirements.every(r => r.met);
  const testRequirements = useMemo(
    () => runRequirements.filter(r => r.id !== 'output_mapping'),
    [runRequirements]
  );
  const allTestRequirementsMet = testRequirements.every(r => r.met);
  const activeAutomationTestRun =
    activeTestRun?.run.automationBlockId === blockId &&
    activeTestRun.run.workflowId === config?.workflowId
      ? activeTestRun
      : null;
  const activeTestSourceDocCount =
    activeAutomationTestRun?.run.sourceDocIds.length ?? 0;
  const activeTestRowCount = Array.isArray(
    (activeAutomationTestRun?.run.inputPayload as { rows?: unknown })?.rows
  )
    ? (
        (activeAutomationTestRun?.run.inputPayload as { rows?: unknown[] })
          .rows ?? []
      ).length
    : 0;
  const activeTestExpiresAt = activeAutomationTestRun
    ? new Date(activeAutomationTestRun.run.expiresAt).toLocaleString()
    : null;

  /**
   * Persist log and update runtime status after execution completes
   * Also fetches workflow engine execution details for nodeSteps
   */
  const handleExecutionComplete = useCallback(
    async (status: 'success' | 'error' | 'cancelled') => {
      // Get the completed log from history (not activeLog, which is null after completeLog)
      // completeLog() adds to history then sets activeLog = null, so we get from history
      const completedLog = loggingService.getLatestLog(blockId);

      if (!completedLog || !blockModel || !blockModel.store) {
        console.warn('[ExecutionPanel] Cannot persist: missing data');
        return;
      }

      // Try to fetch nodeSteps from workflow engine execution details
      if (workflow?.id && completedLog.process) {
        try {
          console.log(
            '[ExecutionPanel] Fetching workflow engine execution details...'
          );
          // Small delay to ensure workflow engine has finished processing
          await new Promise(resolve => setTimeout(resolve, 500));

          const execution = await workflowClient.getLatestExecution(
            workflow.id
          );
          if (execution) {
            const nodeSteps =
              workflowClient.parseExecutionToNodeSteps(execution);
            console.log('[ExecutionPanel] Got nodeSteps:', nodeSteps.length);

            // Update the log with nodeSteps
            completedLog.process.nodeSteps = nodeSteps;
            completedLog.process.executionId = execution.id;
          }
        } catch (error) {
          console.warn(
            '[ExecutionPanel] Failed to fetch execution details:',
            error
          );
        }
      }

      try {
        // Persist log to backend (Single Source of Truth)
        // This is done HERE after fetching nodeSteps so all execution data is included
        const backendStatus =
          status === 'success'
            ? 'completed'
            : status === 'error'
              ? 'failed'
              : 'cancelled';

        const response = await workspaceApiFetch(
          workflowWorkspaceRoutes.logs(workspaceId),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              workspaceId,
              docId,
              blockId,
              mode: completedLog.mode,
              status: backendStatus,
              startedAt: new Date(completedLog.startedAt).toISOString(),
              completedAt: new Date(
                completedLog.completedAt || Date.now()
              ).toISOString(),
              inputStage: completedLog.input
                ? {
                    sourceTableId: completedLog.input.sourceTableId,
                    sourceTableName: completedLog.input.sourceTable,
                    rowCount: completedLog.input.passedFilter,
                    rows: completedLog.input.payloads,
                  }
                : undefined,
              processStage: completedLog.process
                ? {
                    webhookUrl:
                      workflow?.webhookUrl || config?.webhookUrlOverride,
                    duration: completedLog.process.duration,
                    responseStatus:
                      completedLog.process.status === 'success' ? 200 : 500,
                    responseData: completedLog.process.response,
                    nodeSteps: completedLog.process.nodeSteps,
                    executionId: completedLog.process.executionId,
                    workflowName: completedLog.process.workflowName,
                  }
                : undefined,
              outputStage: completedLog.output
                ? {
                    targetTableId: completedLog.output.targetTableId,
                    targetTableName: completedLog.output.targetTable,
                    rowsWritten: completedLog.output.rowsWritten,
                  }
                : undefined,
              error: completedLog.error,
            }),
          },
          {
            workspaceId,
            category: 'foreground-action',
            mode: 'foreground',
            priority: 'foreground',
          }
        );

        if (!response.ok) {
          console.error(
            '[ExecutionPanel] Failed to persist log to backend:',
            response.status
          );
        } else {
          console.log(
            '[ExecutionPanel] Log persisted to backend with nodeSteps'
          );
        }
      } catch (error) {
        console.error('[ExecutionPanel] Error persisting log:', error);
      }
    },
    [
      loggingService,
      blockId,
      blockModel,
      workflow?.id,
      workflowClient,
      workspaceId,
      docId,
      workflow?.webhookUrl,
      config?.webhookUrlOverride,
    ]
  );

  // E029-PREFLIGHT: Pre-Flight Sync with local verification + backend verification
  const workspace = workspaceService.workspace;
  const configApi = useService(AutomationConfigApiService);

  /**
   * E029-PREFLIGHT: Shared pre-flight sync for execute and side-effect-free
   * workflow test data preparation.
   *
   * 1. Verify local block props contain required fields
   * 2. Flush Y.js to backend (flushAndWaitForSynced)
   * 3. Call verify-sync endpoint - backend reads Y.js and returns what it sees
   * 4. Compare local vs backend - field-by-field based on profile
   *
   * Profile: 'execute' — full config comparison including output writes.
   * Profile: 'test' — source/workflow/input comparison only.
   */
  const preFlightSync = useCallback(
    async (
      operationName: string,
      profile: PreFlightProfile
    ): Promise<boolean> => {
      // Reset state
      setProgressPhase('preflight');
      setPreFlightExpanded(false);

      const steps: PreFlightStep[] = [
        {
          name: t['com.streamlain.streamlain.execution.step.config-check'](),
          status: 'pending',
        },
        { name: 'Y.js Sync', status: 'pending' },
        {
          name: t['com.streamlain.streamlain.execution.step.backend-verify'](),
          status: 'pending',
        },
        {
          name: t['com.streamlain.streamlain.execution.step.config-compare'](),
          status: 'pending',
        },
      ];

      const updateResult = (partial: Partial<PreFlightResult>) => {
        const current: PreFlightResult = {
          profile,
          steps: [...steps],
          attributes: [],
          totalAttributes: 0,
          matchedAttributes: 0,
          overallSuccess: false,
          ...partial,
        };
        preFlightRef.current = current;
        setPreFlightResult({ ...current });
      };

      const failStep = (idx: number, error: string) => {
        steps[idx].status = 'failed';
        steps[idx].error = error;
        updateResult({ overallSuccess: false });
      };

      const notifyBackendUnavailable = () => {
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
            'Ausführung gerade nicht verfügbar'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-message',
            'Der Backend-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.'
          ),
        });
      };

      const notifySyncNotReady = () => {
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.prepare-failed-title',
            'Ausführung noch nicht möglich'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.sync-not-ready-message',
            'Die Änderungen sind noch nicht vollständig synchronisiert. Bitte gleich erneut versuchen.'
          ),
        });
      };

      // Publish initial state
      updateResult({});

      // Step 1: Config Check
      const t1 = performance.now();
      steps[0].status = 'running';
      updateResult({});

      if (!blockModel) {
        failStep(0, 'Block model not available');
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.block-not-found'](),
          message: 'Automation block model is not available.',
        });
        return false;
      }

      const localConfig = extractConfig(blockModel);
      const hasLocalSource =
        localConfig?.triggerConfig?.type === 'webhook'
          ? !!localConfig?.triggerConfig?.webhookCredentialId
          : !!localConfig?.sourceTableId;
      if (!hasLocalSource) {
        failStep(
          0,
          localConfig?.triggerConfig?.type === 'webhook'
            ? 'No webhook source configured'
            : 'No source table configured'
        );
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.config-incomplete'](),
          message:
            localConfig?.triggerConfig?.type === 'webhook'
              ? (t[
                  'com.streamlain.streamlain.execution.notify.webhook-source-required'
                ]?.() ??
                'No webhook source configured. Please select a webhook first.')
              : 'No source table configured. Please select an input table.',
        });
        return false;
      }
      if (!localConfig?.workflowId && !localConfig?.webhookUrlOverride) {
        failStep(0, 'No workflow connected');
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.config-incomplete'](),
          message: 'No workflow connected. Please select a workflow first.',
        });
        return false;
      }

      if (
        profile === 'execute' &&
        localConfig.statusTracking?.enabled &&
        !localConfig.statusTracking.columnId
      ) {
        failStep(0, 'Status tracking column not configured');
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.config-incomplete'](),
          message:
            t[
              'com.streamlain.streamlain.validation.status-tracking-incomplete'
            ](),
        });
        return false;
      }

      const staleMappings = getStaleWorkflowMappingIds(
        profile,
        localConfig,
        selectedWorkflowSchema
      );
      if (staleMappings.input.length > 0) {
        failStep(0, 'Stale input mappings');
        notify.error({
          title: 'Input mapping invalid',
          message:
            'Workflow input fields changed. Please remap the Input tab before executing.',
        });
        return false;
      }

      if (staleMappings.output.length > 0) {
        failStep(0, 'Stale output mappings');
        notify.error({
          title: 'Output mapping invalid',
          message:
            'Workflow output fields changed. Please remap the Output tab before executing.',
        });
        return false;
      }

      if (profile === 'execute') {
        // Check for unconfigured output targets (empty tableId)
        const emptyTargets = (localConfig.outputTables ?? []).filter(
          t => !t.tableId
        );
        if (emptyTargets.length > 0) {
          failStep(0, `${emptyTargets.length} unconfigured output target(s)`);
          notify.error({
            title: 'Unconfigured output target',
            message:
              'One or more output targets have no table selected. ' +
              'Please select a table or remove the empty target.',
          });
          return false;
        }

        // Check for output field mappings (at least one responseField must be set)
        if (!(localConfig.outputMappings ?? []).some(m => !!m.responseField)) {
          failStep(0, 'No output field mapping configured');
          notify.error({
            title: 'No output mapping',
            message:
              'Map at least one response field to a target column in the Output tab.',
          });
          return false;
        }
      }

      steps[0].status = 'done';
      steps[0].durationMs = Math.round(performance.now() - t1);
      updateResult({});

      // Step 2: Y.js Flush
      const t2 = performance.now();
      steps[1].status = 'running';
      updateResult({});

      try {
        await flushPreFlightDocIds(
          workspace.engine.doc,
          collectPreFlightDocIds(
            docId,
            localConfig,
            profile,
            preFlightDatabaseStorageRefs,
            workspaceId
          )
        );
      } catch (syncError) {
        failStep(
          1,
          syncError instanceof Error ? syncError.message : 'Flush failed'
        );
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.prepare-failed-title',
            'Ausführung noch nicht möglich'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.sync-not-ready-message',
            'Die Änderungen sind noch nicht vollständig synchronisiert. Bitte gleich erneut versuchen.'
          ),
        });
        return false;
      }

      steps[1].status = 'done';
      steps[1].durationMs = Math.round(performance.now() - t2);
      updateResult({});

      // Step 3: Backend Verify
      const t3 = performance.now();
      steps[2].status = 'running';
      updateResult({});

      let backendConfig: VerifySyncConfig;
      try {
        const verifyResult = await configApi.verifySyncConfig(
          blockId,
          workspaceId,
          docId
        );

        if (!verifyResult.verified || !verifyResult.config) {
          failStep(2, verifyResult.error || 'Backend verification failed');
          if (isVerifySyncBackendUnavailable(verifyResult)) {
            notifyBackendUnavailable();
          } else {
            notifySyncNotReady();
          }
          return false;
        }

        backendConfig = verifyResult.config;
      } catch (verifyError) {
        failStep(
          2,
          verifyError instanceof Error
            ? verifyError.message
            : 'Verify request failed'
        );
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
            'Ausführung gerade nicht verfügbar'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-message',
            'Der Backend-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.'
          ),
        });
        return false;
      }

      steps[2].status = 'done';
      steps[2].durationMs = Math.round(performance.now() - t3);
      updateResult({});

      // Step 4: Config Compare (profile-dependent)
      // Auto-retry once on mismatch: sporadic sync races can cause the backend
      // snapshot to lag behind the frontend Y.js state despite flush succeeding.
      const MAX_COMPARE_ATTEMPTS = 2;
      const COMPARE_RETRY_DELAY_MS = 500;
      let compareAttempt = 0;
      let attributes: PreFlightAttribute[] = [];
      let matchedCount = 0;
      let allMatch = false;

      const t4 = performance.now();
      steps[3].status = 'running';
      updateResult({});

      while (compareAttempt < MAX_COMPARE_ATTEMPTS) {
        attributes = buildPreFlightAttributes(
          profile,
          localConfig,
          backendConfig
        );

        matchedCount = attributes.filter(a => a.match).length;
        allMatch = matchedCount === attributes.length;

        if (allMatch || compareAttempt >= MAX_COMPARE_ATTEMPTS - 1) break;

        // Retry: wait, re-flush, re-verify
        console.log(
          `[Preflight] Config Compare mismatch (attempt ${compareAttempt + 1}), retrying in ${COMPARE_RETRY_DELAY_MS}ms...`
        );
        await new Promise(r => setTimeout(r, COMPARE_RETRY_DELAY_MS));
        try {
          await flushPreFlightDocIds(
            workspace.engine.doc,
            collectPreFlightDocIds(
              docId,
              localConfig,
              profile,
              preFlightDatabaseStorageRefs,
              workspaceId
            )
          );
          const retryResult = await configApi.verifySyncConfig(
            blockId,
            workspaceId,
            docId
          );
          if (isVerifySyncBackendUnavailable(retryResult)) {
            steps[3].status = 'failed';
            steps[3].durationMs = Math.round(performance.now() - t4);
            steps[3].error = retryResult.error || 'Backend verification failed';
            updateResult({ overallSuccess: false });
            notifyBackendUnavailable();
            return false;
          }
          if (retryResult.verified && retryResult.config) {
            backendConfig = retryResult.config;
          }
        } catch {
          // Retry failed — fall through to mismatch handling
        }
        compareAttempt++;
      }

      if (!allMatch) {
        steps[3].status = 'failed';
        steps[3].durationMs = Math.round(performance.now() - t4);
        steps[3].error = `${attributes.length - matchedCount} mismatch(es)`;
        updateResult({
          attributes,
          totalAttributes: attributes.length,
          matchedAttributes: matchedCount,
          overallSuccess: false,
        });

        const mismatches = attributes
          .filter(a => !a.match)
          .map(a => a.name)
          .join(', ');
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.prepare-failed-title',
            'Ausführung noch nicht möglich'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.config-mismatch-message',
            'Die Automation ist noch nicht vollständig synchronisiert. Bitte gleich erneut versuchen.'
          ),
        });
        console.warn('[ExecutionPanel] Config sync mismatch:', mismatches);
        setPreFlightExpanded(true);
        return false;
      }

      steps[3].status = 'done';
      steps[3].durationMs = Math.round(performance.now() - t4);
      updateResult({
        attributes,
        totalAttributes: attributes.length,
        matchedAttributes: matchedCount,
        overallSuccess: true,
      });

      console.log(
        `[ExecutionPanel] ${operationName}: Pre-flight OK (${profile}, ${matchedCount}/${attributes.length})`
      );

      // Transition to execution phase
      setProgressPhase('execution');
      return true;
    },
    [
      blockModel,
      workspace,
      docId,
      workspaceId,
      blockId,
      configApi,
      selectedWorkflowSchema,
      preFlightDatabaseStorageRefs,
      t,
    ]
  );

  // T047: Calculate actual row count based on batchSize
  const batchSize = config?.batchConfig?.batchSize ?? 1;
  const actualRowCount = Math.min(rows.length, batchSize);
  const isBatchMode = config?.batchConfig?.executionMode === 'batch';

  // E029-PREFLIGHT: Execute with full profile
  const handleExecute = useCallback(async (): Promise<void> => {
    // sl-3yai.7.2 + sl-za0v.10: Block execution if compliance guard or RBAC fails
    if (!canExecute) {
      notify.error({
        title:
          t[
            'com.streamlain.streamlain.execution.notify.permission-denied-title'
          ](),
        message:
          t[
            'com.streamlain.streamlain.execution.notify.permission-denied-message'
          ](),
      });
      return;
    }

    const synced = await preFlightSync('Execute', 'execute');
    if (!synced) return;

    executionService
      .executeViaBackend(workspaceId, docId, blockId, 'execute', actualRowCount)
      .then(result => {
        if (!result.success) {
          // Failure is already surfaced by AutomationSocketService via the
          // `execution:update` websocket event (classified message + "Open
          // Workflow" action). Showing a second toast here spammed the user
          // with three notifications for the same event (see sl-oe5x smoke
          // test). Fall through so `refreshLogs` below still runs.
        } else if (result.warnings && result.warnings.length > 0) {
          // sl-g3on.5.3: Surface backend warnings as a single toast
          // ("N warnings — see Execution panel for details") and stash
          // them on local state so the panel renders one WarningRow per
          // entry. The detail view is the source of truth; the toast is
          // only the wakeup hint.
          setLastWarnings(result.warnings);
          notify.warning({
            title: t['com.streamlain.streamlain.execution.warnings.title']({
              count: String(result.warnings.length),
            }),
            message:
              t['com.streamlain.streamlain.execution.warnings.see-panel'](),
          });
        } else {
          setLastWarnings(null);
        }
        loggingService.refreshLogs(blockId, workspaceId);
        // sl-g3on.9.2: Pull per-row retry metadata from the most recent
        // log right after refresh — it carries the rows[] array the
        // backend orchestrator now persists into processStage.
        const latest = loggingService.getLatestLog(blockId);
        const rows = latest?.process?.rows;
        setLastExecutionRows(rows && rows.length > 0 ? rows : null);
      })
      .catch((error: Error) => {
        console.error('Execution failed:', error);
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
            'Ausführung gerade nicht verfügbar'
          ),
          message: userFacingExecutionError(t, error.message),
        });
        loggingService.refreshLogs(blockId, workspaceId);
      });
  }, [
    preFlightSync,
    executionService,
    workspaceId,
    docId,
    blockId,
    loggingService,
    canExecute,
    t,
    actualRowCount,
  ]);

  const handleCreateTestRun = useCallback(async (): Promise<void> => {
    if (!canExecute) {
      notify.error({
        title:
          t[
            'com.streamlain.streamlain.execution.notify.permission-denied-title'
          ](),
        message:
          t[
            'com.streamlain.streamlain.execution.notify.permission-denied-message'
          ](),
      });
      return;
    }

    setCreatingTestRun(true);
    try {
      const synced = await preFlightSync('Test', 'test');
      if (!synced) return;

      const run = await workflowTestContext.createFromAutomation({
        workspaceId,
        docId,
        blockId,
      });
      notify.success({
        title:
          t['com.streamlain.streamlain.execution.test.created-title']?.() ??
          'Test-Kontext bereit',
        message:
          t['com.streamlain.streamlain.execution.test.created-message']?.() ??
          'Der Workflow Builder kann jetzt Step-Previews mit Automation-Daten anzeigen.',
      });
      console.log('[ExecutionPanel] Workflow test run created', {
        testRunId: run.run.id,
        workflowId: run.run.workflowId,
        sourceDocIds: run.run.sourceDocIds,
      });
      setShowTestDialog(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Could not create test run';
      notify.error({
        title:
          t[
            'com.streamlain.streamlain.execution.test.create-failed-title'
          ]?.() ?? 'Test-Kontext fehlgeschlagen',
        message,
      });
    } finally {
      setCreatingTestRun(false);
    }
  }, [
    canExecute,
    t,
    preFlightSync,
    workflowTestContext,
    workspaceId,
    docId,
    blockId,
  ]);

  const handleStop = useCallback(() => {
    executionService.stop(blockId, workspaceId);
    // Persist log when cancelled
    handleExecutionComplete('cancelled').catch(console.error);
  }, [executionService, blockId, workspaceId, handleExecutionComplete]);

  const isIdle = state.status === 'idle';
  const isRunning = state.status === 'running' || state.status === 'queued';
  const isFinished =
    state.status === 'completed' ||
    state.status === 'cancelled' ||
    state.status === 'failed';

  // Track execution start time for elapsed timer
  const executionStartRef = useRef<number | null>(null);
  if (isRunning && executionStartRef.current === null) {
    executionStartRef.current = Date.now();
  } else if (isIdle && !isFinished && executionStartRef.current !== null) {
    executionStartRef.current = null;
  }
  const elapsedTime = useElapsedTimer(isRunning, executionStartRef.current);

  // sl-3nkm.8.4: Load the workspace's plan-based workflow limits once
  // per panel mount so the timeout-warning hook can drive bar colour
  // and tooltip copy. Same source LimitsSection uses (GraphQL
  // workspace.workflowLimits). Null while loading → hook falls back
  // to `normal`, so the pre-8.4 visual is preserved when limits are
  // unknown.
  const [workflowLimits, setWorkflowLimits] = useState<WorkflowLimits | null>(
    null
  );
  useEffect(() => {
    let cancelled = false;
    void fetchWorkflowLimits(workspaceId).then(data => {
      if (!cancelled) setWorkflowLimits(data);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // sl-3nkm.8.4: `maxStepDurationMs` is PER step. Only media steps
  // expose a step-level signal on the frontend
  // (`state.progress.media`); the backend never clears it mid-run,
  // and batch execution REUSES `stepId` across rows — only
  // `executionId` is row-unique (see
  // `AutomationBatchExecutor.executeOne`). We anchor on
  // `executionId` so the warning re-arms on every row, and drop
  // the anchor once `percent` hits 100 so a following non-media
  // step does not inherit the previous step's start time.
  // Non-media steps get no warning — preferable to a
  // confidently-wrong one driven off run-start elapsed.
  const mediaExecutionId = state.progress.media?.executionId ?? null;
  const mediaPercent = state.progress.media?.percent ?? 0;
  const stepStartRef = useRef<number | null>(null);
  const lastExecutionIdRef = useRef<string | null>(null);
  const completedExecutionIdRef = useRef<string | null>(null);
  if (!isRunning) {
    stepStartRef.current = null;
    lastExecutionIdRef.current = null;
    completedExecutionIdRef.current = null;
  } else if (
    mediaExecutionId &&
    mediaExecutionId !== lastExecutionIdRef.current
  ) {
    lastExecutionIdRef.current = mediaExecutionId;
    if (mediaPercent >= 100) {
      // First event delivered for this row is already the terminal
      // frame (fast step / throttle). Do not arm — the step is
      // done. Guards against a stale anchor outliving the row.
      stepStartRef.current = null;
      completedExecutionIdRef.current = mediaExecutionId;
    } else {
      stepStartRef.current = Date.now();
      completedExecutionIdRef.current = null;
    }
  } else if (
    mediaExecutionId &&
    mediaExecutionId === lastExecutionIdRef.current &&
    mediaPercent >= 100 &&
    completedExecutionIdRef.current !== mediaExecutionId
  ) {
    stepStartRef.current = null;
    completedExecutionIdRef.current = mediaExecutionId;
  }
  const timeoutWarning = useTimeoutWarning(
    isRunning,
    stepStartRef.current,
    workflowLimits?.maxStepDurationMs ?? null
  );

  return (
    <div className={styles.section}>
      {/* Run Requirements Dialog - shows what's missing */}
      <RunRequirementsDialog
        open={requirementsDialogMode !== null}
        onOpenChange={open => {
          if (!open) setRequirementsDialogMode(null);
        }}
        requirements={
          requirementsDialogMode === 'test' ? testRequirements : runRequirements
        }
      />

      <TestRunConsentDialog
        open={showTestDialog}
        loading={creatingTestRun}
        onCancel={() => setShowTestDialog(false)}
        onConfirm={() => void handleCreateTestRun()}
      />

      {/* Start Controls (when idle or finished) */}
      {(isIdle || isFinished) && (
        <>
          <div style={{ display: 'flex', gap: '8px' }}>
            {hasActionItems ? (
              <>
                <div
                  className={styles.executionButtonFrame}
                  style={{ cursor: 'not-allowed' }}
                  title={
                    !isOnline
                      ? offlineExecutionMessage
                      : (t[
                          'com.streamlain.streamlain.execution.disabled-hint'
                        ]?.() ?? 'Complete the steps below first')
                  }
                  onClick={
                    !isOnline ? showOfflineExecutionToast : shakeActionBox
                  }
                >
                  {!isOnline && (
                    <span
                      className={styles.executionOfflineBadge}
                      aria-hidden="true"
                    >
                      !
                    </span>
                  )}
                  <div style={{ pointerEvents: 'none', opacity: 0.5 }}>
                    <Button style={{ width: '100%' }}>
                      {t['com.streamlain.streamlain.execution.btn.test']?.() ??
                        'Testen'}
                    </Button>
                  </div>
                </div>
                <div
                  className={styles.executionButtonFrame}
                  style={{ cursor: 'not-allowed' }}
                  title={
                    !isOnline
                      ? offlineExecutionMessage
                      : (t[
                          'com.streamlain.streamlain.execution.disabled-hint'
                        ]?.() ?? 'Complete the steps below first')
                  }
                  onClick={
                    !isOnline ? showOfflineExecutionToast : shakeActionBox
                  }
                >
                  {!isOnline && (
                    <span
                      className={styles.executionOfflineBadge}
                      aria-hidden="true"
                    >
                      !
                    </span>
                  )}
                  <div style={{ pointerEvents: 'none', opacity: 0.5 }}>
                    <Button variant="primary" style={{ width: '100%' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <PlayIcon />
                        {t['com.streamlain.streamlain.execution.btn.run']()}
                      </span>
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div
                  className={styles.executionButtonFrame}
                  style={!isOnline ? { cursor: 'not-allowed' } : undefined}
                  title={!isOnline ? offlineExecutionMessage : undefined}
                  onClick={!isOnline ? showOfflineExecutionToast : undefined}
                >
                  {!isOnline && (
                    <span
                      className={styles.executionOfflineBadge}
                      aria-hidden="true"
                    >
                      !
                    </span>
                  )}
                  <Button
                    disabled={!canStart || creatingTestRun}
                    onClick={() => {
                      if (allTestRequirementsMet) {
                        setShowTestDialog(true);
                      } else {
                        setRequirementsDialogMode('test');
                      }
                    }}
                    style={{
                      width: '100%',
                      pointerEvents: !isOnline ? 'none' : undefined,
                    }}
                  >
                    {creatingTestRun
                      ? (t[
                          'com.streamlain.streamlain.execution.test.creating'
                        ]?.() ?? 'Bereite vor...')
                      : (t[
                          'com.streamlain.streamlain.execution.btn.test'
                        ]?.() ?? 'Testen')}
                  </Button>
                </div>
                <div
                  className={styles.executionButtonFrame}
                  style={!isOnline ? { cursor: 'not-allowed' } : undefined}
                  title={!isOnline ? offlineExecutionMessage : undefined}
                  onClick={!isOnline ? showOfflineExecutionToast : undefined}
                >
                  {!isOnline && (
                    <span
                      className={styles.executionOfflineBadge}
                      aria-hidden="true"
                    >
                      !
                    </span>
                  )}
                  <Button
                    variant="primary"
                    disabled={!canStart}
                    onClick={() => {
                      if (allRequirementsMet) {
                        handleExecute().catch(console.error);
                      } else {
                        setRequirementsDialogMode('run');
                      }
                    }}
                    style={{
                      width: '100%',
                      pointerEvents: !isOnline ? 'none' : undefined,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <PlayIcon />
                      {allRequirementsMet
                        ? t[
                            'com.streamlain.streamlain.execution.btn.run-count'
                          ]({
                            count: String(actualRowCount),
                            array: isBatchMode
                              ? t[
                                  'com.streamlain.streamlain.execution.btn.run-array-suffix'
                                ]()
                              : '',
                          })
                        : t['com.streamlain.streamlain.execution.btn.run']()}
                    </span>
                  </Button>
                </div>
              </>
            )}
          </div>

          {activeAutomationTestRun && (
            <div
              style={{
                marginTop: '10px',
                padding: '8px 10px',
                borderRadius: '8px',
                border: `1px solid color-mix(in srgb, ${cssVarV2('button/primary')} 35%, transparent)`,
                background: `color-mix(in srgb, ${cssVarV2('button/primary')} 8%, transparent)`,
                color: cssVarV2('text/secondary'),
                fontSize: '11px',
                lineHeight: 1.45,
              }}
            >
              <strong style={{ color: cssVarV2('text/primary') }}>
                {t[
                  'com.streamlain.streamlain.execution.test.context-active'
                ]?.() ?? 'Test-Kontext aktiv'}
              </strong>{' '}
              {t['com.streamlain.streamlain.execution.test.context-summary']?.({
                rows: String(activeTestRowCount),
                docs: String(activeTestSourceDocCount),
                expiresAt: activeTestExpiresAt ?? '',
              }) ??
                `${activeTestRowCount} Zeile(n), ${activeTestSourceDocCount} Quelldokument(e), bis ${activeTestExpiresAt}`}
            </div>
          )}

          {/* Missing steps box with action buttons */}
          {hasActionItems && (
            <div
              ref={actionBoxRef}
              style={{
                marginTop: '10px',
                padding: '10px 12px',
                background: `color-mix(in srgb, ${cssVarV2('others/warnLabel/yellow')} 8%, transparent)`,
                border: `1px solid color-mix(in srgb, ${cssVarV2('others/warnLabel/yellow')} 40%, transparent)`,
                borderRadius: '8px',
                fontSize: '12px',
                lineHeight: '1.4',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke={cssVarV2('others/warnLabel/yellow')}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {actionItems!.map(item => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={item.onClick}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      border: `1px solid ${cssVarV2('button/primary')}`,
                      background: cssVarV2('button/primary'),
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 500,
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Runtime Controls (when running) */}
      {isRunning && (
        <div style={{ display: 'flex', gap: '8px' }}>
          {canStop && (
            <Button onClick={handleStop} style={{ flex: 1 }}>
              <CloseIcon style={{ marginRight: '4px' }} />
              {t['com.streamlain.streamlain.execution.btn.stop']()}
            </Button>
          )}
        </div>
      )}

      {/* Progress (only when configured or has run history) */}
      {(!actionItems ||
        actionItems.length === 0 ||
        isRunning ||
        isFinished) && (
        <div
          style={{
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          <TimeoutAwareProgressBar
            percentage={state.progress.percentage}
            status={state.status}
            phase={progressPhase}
            preFlightResult={preFlightResult}
            timeoutState={timeoutWarning.state}
            remainingMs={timeoutWarning.remainingMs}
          />
          <ProgressStats
            state={state}
            preFlightResult={preFlightResult}
            phase={progressPhase}
            onPreFlightClick={() =>
              preFlightResult && setPreFlightExpanded(v => !v)
            }
            elapsedTime={elapsedTime}
          />
          {preFlightResult && (
            <PreFlightExpandable
              result={preFlightResult}
              expanded={preFlightExpanded}
            />
          )}
        </div>
      )}

      {/* sl-g3on.9.2: Per-row retry / on-failure metadata */}
      {lastExecutionRows && lastExecutionRows.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          <ExecutionRowList rows={lastExecutionRows} />
        </div>
      )}

      {/* sl-g3on.5.3: Backend execution warnings (i18n + structured params) */}
      {lastWarnings && lastWarnings.length > 0 && (
        <div
          style={{
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
          data-testid="execution-warnings-list"
        >
          {lastWarnings.map((w, idx) => (
            <WarningRow
              key={`${w.key}-${idx}`}
              warning={w}
              workflowSchema={selectedWorkflowSchema}
            />
          ))}
        </div>
      )}

      {/* Error Message (show when error exists, even if idle) */}
      {state.error &&
        (() => {
          const keyError = isAiKeyError(state.error);
          const displayError = keyError.isKeyError
            ? state.error
            : userFacingExecutionError(t, state.error);
          return (
            <div
              style={{
                marginTop: '12px',
                padding: '8px 12px',
                backgroundColor: cssVarV2('layer/background/error'),
                borderRadius: '8px',
                fontSize: '12px',
                color: cssVarV2('status/error'),
              }}
            >
              {displayError}
              {keyError.isKeyError && (
                <div style={{ marginTop: '6px' }}>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      workspaceDialogService.open('setting', {
                        activeTab: 'workspace:ai',
                      })
                    }
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        workspaceDialogService.open('setting', {
                          activeTab: 'workspace:ai',
                        });
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: cssVarV2('text/link'),
                      fontSize: '12px',
                    }}
                  >
                    {t[
                      'com.streamlain.streamlain.execution.open-ai-settings'
                    ]?.() ??
                      t[
                        'com.streamlain.streamlain.execution.configure-ai-keys'
                      ]()}
                  </span>
                </div>
              )}
            </div>
          );
        })()}
    </div>
  );
};
